// static/js/components/ui/AttachmentButton.jsx

/**
 * @file AttachmentButton.jsx
 * @module components/ui/AttachmentButton
 * 
 * @description 
 * A React component that renders a button for attaching datasets from 
 * Assistant messages. Supports attaching CSV, JSON, and Markdown files 
 * while filtering out image attachments (for now, until webapp is migrated to Responses API). 
 * Displays an appropriate icon and tooltip text based on the attachment state.
 * 
 * @usage
 * ```jsx
 * <AttachmentButton 
 *   datasetTypes={{ csv: true, json: true }} 
 *   onAttach={(selected) => console.log(selected)} 
 *   isAttached={false} 
 * />
 * ```
 * 
 * @component
 * @example
 * ```jsx
 * <AttachmentButton 
 *   datasetTypes={{ csv: true, json: false, markdown: true }} 
 *   onAttach={handleAttach} 
 *   isAttached={true} 
 * />
 * ```
 * 
 * @param {Object} props - Component props
 * @param {Object} props.datasetTypes - Available dataset types to attach (e.g., `{ csv: true, json: false }`)
 * @param {Function} props.onAttach - Callback when a dataset type is toggled
 * @param {boolean} props.isAttached - Indicates whether a dataset is currently attached
 * 
 * @returns {JSX.Element | null} A button for dataset attachment, or null if no valid dataset types exist.
 * 
 * @exports AttachmentButton
 */

import React from 'react';
import { Paperclip, Check } from 'lucide-react';

/**
 * AttachmentButton Component
 * 
 * Renders a button for attaching datasets from Assistant messages.
 * 
 * @param {Object} props - Component props
 * @param {Object} props.datasetTypes - Available dataset types to attach
 * @param {Function} props.onAttach - Callback when datasets are selected
 * @param {boolean} props.isAttached - Whether any dataset is currently attached
 */
const AttachmentButton = ({ 
  datasetTypes = {},
  onAttach = () => {}, // Default empty function to prevent TypeError
  isAttached = false 
}) => {
  // Filter out 'image' type from datasetTypes (hides Attach image button)
  const filteredTypes = Object.fromEntries(
    Object.entries(datasetTypes).filter(([type]) => type !== 'image')
  );
  
  // Count available dataset types using filteredTypes instead of datasetTypes
  const typeCount = Object.keys(filteredTypes).filter(key => filteredTypes[key]).length;

  // Check if we have any types left after filtering
  if (typeCount === 0) {
    // If no allowed types remain after filtering, don't render anything
    return null;
  }

  // Get type-specific tooltip text (hover text)
  const getTooltipText = (type) => {
    if (isAttached) {
      switch (type) {
        case 'csv':
          return 'CSV data attached';
        case 'json':
          return 'JSON data attached';
        case 'markdown':
          return 'Document attached';
        default:
          return 'Dataset attached';
      }
    }

    switch (type) {
      case 'csv':
        return 'Attach CSV data';
      case 'json':
        return 'Attach JSON data';
      case 'markdown':
        return 'Attach document';
      default:
        return 'Attach dataset';
    }
  };

  // Handle single type toggle
  const handleSingleTypeToggle = (type) => {
    // Toggle the attachment state
    onAttach({ [type]: !isAttached });
  };

  // Get the first available type - we'll only support a single dataset attachment
  const type = Object.keys(filteredTypes)[0];

  return (
    <button
      onClick={() => handleSingleTypeToggle(type)}  // toggles attachment state
      className={`p-2 rounded-lg transition-colors ${
        isAttached 
          ? 'text-primary-button bg-primary-700/20' 
          : 'text-primary-text hover:bg-primary-700/20'
      }`}
      title={getTooltipText(type)} // type-specific tooltip text
    >
      {isAttached ? <Check size={16} /> : <Paperclip size={16} />}
    </button>
  );
};

export default AttachmentButton;